import React from 'react';
import { mailMergeApi } from '../../requests/mailMerge';
import {
    TOKENS, tokenChipInsertHtml, MODE_ALL, MODE_ONE, MODE_SUMMARY, SUMMARY_PHRASE,
    ATTACHMENT_EXTENSIONS, MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS, isAttachableName, humanSize,
} from './merge';
import { SUMMIT_LINKS } from './templates';

const LinkIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M5.5 8.5 L8.5 5.5"/>
        <path d="M6.5 4 L8 2.5 a2.1 2.1 0 0 1 3 3 L9.5 7"/>
        <path d="M7.5 10 L6 11.5 a2.1 2.1 0 0 1 -3 -3 L4.5 7"/>
    </svg>
);

const PaperclipIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M11.5 6.5 L7 11 a2.6 2.6 0 0 1 -3.7 -3.7 L8 2.6 a1.7 1.7 0 0 1 2.4 2.4 L5.7 9.7 a0.8 0.8 0 0 1 -1.2 -1.2 L8.5 4.5"/>
    </svg>
);

const BulletIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
        <circle cx="2.4" cy="3" r="1" fill="currentColor" stroke="none"/>
        <circle cx="2.4" cy="7" r="1" fill="currentColor" stroke="none"/>
        <circle cx="2.4" cy="11" r="1" fill="currentColor" stroke="none"/>
        <path d="M5.4 3 H12.5 M5.4 7 H12.5 M5.4 11 H12.5"/>
    </svg>
);

const NumberIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3">
        <path d="M1.6 4 L2.6 3.2 V5.4 M1.6 9 H3.2 L1.6 11 H3.2" strokeWidth="1.1"/>
        <path d="M5.4 3.5 H12.5 M5.4 7 H12.5 M5.4 10.5 H12.5"/>
    </svg>
);

/**
 * Subject, formatting toolbar and body.
 *
 * Both fields are contenteditables React never re-renders: they are seeded
 * once on mount and read back on input. Letting React own the children would
 * move the caret on every keystroke.
 *
 * The subject is one of them rather than an <input> because a merge token has
 * to be an unbreakable chip wherever it is written, and an input can only hold
 * text a typo can silently break.
 */
export default function Composer({
    setup = null,
    subject,
    onSubjectChange,
    initialBodyHtml,
    onBodyChange,
    attachments,
    onAttachmentsChange,
    sender,
    disabled,
    disabledReason,
    // Blocks the send but not the test: a list over the limit can still be proofed.
    sendDisabled,
    onTest,
    onSend,
    onConnect,
    onDisconnect,
    sendLabel,
    propertyMode,
    onPropertyMode,
    busy,
    tokens = TOKENS,
    showPropertyModes = true,
}) {
    const subjectRef = React.useRef(null);
    const bodyRef = React.useRef(null);
    // Which field a token lands in — whichever the caret was last in.
    const activeRef = React.useRef(null);
    const rangeRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    // Names of the files still going up, and why the last pick was turned away.
    const [uploading, setUploading] = React.useState([]);
    const [attachError, setAttachError] = React.useState(null);

    React.useEffect(() => {
        const subjectField = subjectRef.current;
        const bodyField = bodyRef.current;

        subjectField.innerHTML = subject;
        bodyField.innerHTML = initialBodyHtml;
        activeRef.current = bodyField;

        onSubjectChange(subjectField.innerHTML);
        onBodyChange(bodyField.innerHTML);

        const remember = () => {
            const selection = document.getSelection();
            if (!selection || !selection.rangeCount) return;

            const field = [subjectField, bodyField].find((candidate) => candidate.contains(selection.anchorNode));
            if (!field) return;

            activeRef.current = field;
            rangeRef.current = selection.getRangeAt(0).cloneRange();
        };

        document.addEventListener('selectionchange', remember);
        return () => document.removeEventListener('selectionchange', remember);
        // Seeded once on purpose — see the note above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const readBack = () => {
        onSubjectChange(subjectRef.current.innerHTML);
        onBodyChange(bodyRef.current.innerHTML);
    };

    const focusActive = () => {
        const field = activeRef.current || bodyRef.current;
        field.focus();

        const selection = document.getSelection();
        selection.removeAllRanges();

        if (rangeRef.current && field.contains(rangeRef.current.commonAncestorContainer)) {
            selection.addRange(rangeRef.current);
            return field;
        }

        const range = document.createRange();
        range.selectNodeContents(field);
        range.collapse(false);
        selection.addRange(range);

        return field;
    };

    const sync = () => {
        const selection = document.getSelection();
        if (selection && selection.rangeCount) rangeRef.current = selection.getRangeAt(0).cloneRange();
        readBack();
    };

    // Formatting only ever applies to the body: a subject line is plain text
    // by the time it reaches an inbox.
    const format = (command, value) => {
        activeRef.current = bodyRef.current;
        focusActive();
        document.execCommand(command, false, value || null);
        sync();
    };

    const insertToken = (token) => {
        focusActive();
        document.execCommand('insertHTML', false, tokenChipInsertHtml(token.key, token.label));
        sync();
    };

    const insertLink = () => {
        const url = window.prompt('Link URL');
        if (url) format('createLink', url);
    };

    // A link is formatting, so like bold it only ever lands in the body.
    const insertSummitLink = (link) => {
        activeRef.current = bodyRef.current;
        focusActive();
        document.execCommand('insertHTML', false, '<a href="' + link.url + '">' + link.text + '</a>&nbsp;');
        sync();
    };

    // A subject is one line: Enter would split it into two the inbox never shows.
    const blockNewlines = (event) => {
        if (event.key === 'Enter') event.preventDefault();
    };

    const keepSelection = (event) => event.preventDefault();

    // Each file goes up as soon as it is picked, one after another so the
    // running total the cap is checked against is never a guess.
    const attachFiles = async (event) => {
        const files = Array.from(event.target.files);
        event.target.value = '';
        if (!files.length) return;

        let current = attachments;
        let total = current.reduce((sum, file) => sum + file.size, 0);
        let refusal = null;

        for (const file of files) {
            if (!isAttachableName(file.name)) {
                refusal = file.name + ' is not a file type that can be attached (' + ATTACHMENT_EXTENSIONS.join(', ') + ').';
            } else if (current.length >= MAX_ATTACHMENTS) {
                refusal = 'At most ' + MAX_ATTACHMENTS + ' files can be attached.';
            } else if (total + file.size > MAX_ATTACHMENT_BYTES) {
                refusal = file.name + ' would take the attachments past 3 MB in total.';
            } else {
                setUploading((names) => names.concat(file.name));
                try {
                    const uploaded = await mailMergeApi.uploadAttachment(file);
                    current = current.concat(uploaded);
                    total += uploaded.size;
                    onAttachmentsChange(current);
                } catch (err) {
                    refusal = file.name + ': ' + err.message;
                }
                setUploading((names) => names.filter((name) => name !== file.name));
            }
        }

        setAttachError(refusal);
    };

    const removeAttachment = (path) => {
        setAttachError(null);
        onAttachmentsChange(attachments.filter((file) => file.path !== path));
    };

    const uploadingNow = uploading.length > 0;

    return (
        <div className="mm-card mm-compose">
            <div className="mm-compose__head">
                <div className="mm-panel__title">Compose</div>
            </div>

            <div className="mm-sender">
                {sender.connected ? (
                    <div>
                        <span>From</span>{sender.name} &lt;{sender.mailbox}&gt;
                        <button className="mm-linkbtn" style={{ marginLeft: 10 }} onClick={onDisconnect} disabled={busy}>
                            Disconnect Outlook
                        </button>
                    </div>
                ) : (
                    <div>
                        <span>From</span>
                        Nothing yet — a merge goes out from your own Outlook, so connect it first.
                        <button className="mm-linkbtn" style={{ marginLeft: 10 }} onClick={onConnect} disabled={busy || !sender.configured}>
                            Connect Outlook
                        </button>
                    </div>
                )}
            </div>

            {(disabled || sendDisabled) && disabledReason ? <div className="mm-alert--danger">{disabledReason}</div> : null}

            {/* Which letter to start from, and the listing it markets. */}
            {setup}

            <div className="mm-field">
                <div className="mm-eyebrow">Subject</div>
                <div
                    className="mm-input mm-subject"
                    ref={subjectRef}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder="Subject line"
                    onInput={sync}
                    onBlur={sync}
                    onKeyDown={blockNewlines}
                />
            </div>

            <div className="mm-toolbar">
                <button className="mm-toolbar__btn" title="Attach files" onClick={() => fileInputRef.current.click()} disabled={uploadingNow}><PaperclipIcon/></button>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept={ATTACHMENT_EXTENSIONS.map((extension) => '.' + extension).join(',')}
                    onChange={attachFiles}
                />
                <div className="mm-toolbar__divider"/>
                <button className="mm-toolbar__btn" title="Bold" onMouseDown={keepSelection} onClick={() => format('bold')}>B</button>
                <button className="mm-toolbar__btn mm-toolbar__btn--italic" title="Italic" onMouseDown={keepSelection} onClick={() => format('italic')}>I</button>
                <button className="mm-toolbar__btn" title="Insert link" onMouseDown={keepSelection} onClick={insertLink}><LinkIcon/></button>
                <button className="mm-toolbar__btn" title="Bulleted list" onMouseDown={keepSelection} onClick={() => format('insertUnorderedList')}><BulletIcon/></button>
                <button className="mm-toolbar__btn" title="Numbered list" onMouseDown={keepSelection} onClick={() => format('insertOrderedList')}><NumberIcon/></button>
                <div className="mm-toolbar__divider"/>
                <span className="mm-toolbar__label">Insert token</span>
                {tokens.map((token) => (
                    <button
                        key={token.key}
                        className="mm-token"
                        onMouseDown={keepSelection}
                        onClick={() => insertToken(token)}
                    >
                        {token.label}
                    </button>
                ))}
                <span className="mm-toolbar__hint">into subject or body, wherever the cursor is</span>
                <div className="mm-toolbar__divider"/>
                <span className="mm-toolbar__label">Insert link</span>
                {SUMMIT_LINKS.map((link) => (
                    <button
                        key={link.label}
                        className="mm-token"
                        title={link.url}
                        onMouseDown={keepSelection}
                        onClick={() => insertSummitLink(link)}
                    >
                        {link.label}
                    </button>
                ))}
            </div>

            {/* An owner of eight buildings does not want eight addresses in one
                letter, so the property tokens can be narrowed to a single one
                or summed up in three words. */}
            {showPropertyModes ? (
            <div className="mm-toolbar mm-toolbar--modes">
                <span className="mm-toolbar__label">Property tokens show</span>
                <button
                    className={'mm-tab' + (propertyMode === MODE_ALL ? ' mm-tab--on' : '')}
                    onClick={() => onPropertyMode(MODE_ALL)}
                >
                    Every property they own in this list
                </button>
                <button
                    className={'mm-tab' + (propertyMode === MODE_ONE ? ' mm-tab--on' : '')}
                    onClick={() => onPropertyMode(MODE_ONE)}
                >
                    Just one, picked per owner
                </button>
                <button
                    className={'mm-tab' + (propertyMode === MODE_SUMMARY ? ' mm-tab--on' : '')}
                    onClick={() => onPropertyMode(MODE_SUMMARY)}
                >
                    The words &ldquo;{SUMMARY_PHRASE}&rdquo;
                </button>
            </div>
            ) : null}

            <div className="mm-editorwrap">
                <div
                    className="mm-editor"
                    ref={bodyRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={sync}
                    onBlur={sync}
                />
            </div>

            {attachments.length || uploadingNow ? <div className="mm-attachments">
                {attachments.map((file) => (
                    <span key={file.path} className="mm-attachment">
                        <span className="mm-attachment__name">{file.name}</span>
                        <span className="mm-attachment__size">{humanSize(file.size)}</span>
                        <button
                            className="mm-attachment__remove"
                            title={'Remove ' + file.name}
                            onClick={() => removeAttachment(file.path)}
                            disabled={uploadingNow}
                        >
                            &times;
                        </button>
                    </span>
                ))}
                {uploading.map((name) => (
                    <span key={'uploading-' + name} className="mm-attachment mm-attachment--uploading">
                        <span className="mm-attachment__name">{name}</span>
                        <span className="mm-attachment__size">Uploading&hellip;</span>
                    </span>
                ))}
            </div> : null}
            {attachError ? <div className="mm-alert--danger">{attachError}</div> : null}

            <div className="mm-compose__actions">
                <button className="mm-linkbtn" onClick={onTest} disabled={disabled || busy || uploadingNow}>Send a test to my inbox</button>
                <div className="mm-spacer"/>
                <button className="mm-btn mm-btn--primary" onClick={onSend} disabled={disabled || sendDisabled || busy || uploadingNow}>{sendLabel}</button>
            </div>
        </div>
    );
}
